<template>
  <div class="marketing-table-container">
    <div class="marketing-races-body">
      <div class="row">
        <div class="col-xs-12">
          <p class="marketing-races-title">Select a state or enter your zip code to get started</p>
        </div>
      </div>
      <datamap-us :states="states" :all-states="options.state" v-model="activeState"></datamap-us>
      <div class="marketing-table-filters">
        <form class="form-inline" @submit.prevent>
          <div class="row">
            <div class="col-xs-12 col-sm-4 col-left">
              <div class="form-group">
                <rr-multiselect
                  :name="'state'"
                  :label-key="'name'"
                  v-model="filters.state"
                  :options="options.state"
                  :limit="1"
                  :placeholder="'State'"
                ></rr-multiselect>
              </div>
            </div>
            <div class="col-xs-12 col-sm-5 col-center">
              <div class="form-group">
                <rr-multiselect
                  id="dogs-city-selector"
                  :name="'city'"
                  :disabled="!activeState"
                  :label-key="'name'"
                  v-model="filters.city"
                  :options="options.city"
                  :placeholder="'Cities'"
                  :placeholder-multi="'marketing.filters.cities_selected'"
                  :collapse-selected="true"
                ></rr-multiselect>
                <popover
                  target="#dogs-city-selector"
                  trigger="hover-focus"
                  :content="'Please select state first'"
                  :enable="!activeState"
                  :custom-class="'popover_select_size'"
                ></popover>
              </div>
            </div>
            <div class="col-xs-12 col-sm-3 col-right">
              <div class="form-group">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Zipcode"
                  name="zipcode"
                  v-model="zipcode"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-xs-12 col-sm-4 col-left">
              <div class="form-group">
                <rr-multiselect
                  :name="'type'"
                  :label-key="'name'"
                  v-model="filters.type"
                  :options="options.type"
                  :placeholder="'Race types'"
                  :placeholder-multi="'marketing.filters.races_selected'"
                  :collapse-selected="true"
                ></rr-multiselect>
              </div>
            </div>
            <div class="col-xs-12 col-sm-5 col-center">
              <div class="form-group">
                <date-picker
                  type="date"
                  v-model="filters.dates"
                  :range="true"
                  value-type="format"
                  format="YYYY-MM-DD"
                  placeholder="Date range"
                ></date-picker>
              </div>
            </div>
            <div class="col-xs-12 col-sm-3 col-right">
              <div class="form-group">
                <rr-multiselect
                  :name="'allowance'"
                  :label-key="'name'"
                  v-model="filters.allowance"
                  :options="options.allowance"
                  :placeholder="'Allowance'"
                  :collapse-selected="true"
                ></rr-multiselect>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-xs-12 col-sm-4 col-sm-offset-8 col-right text-right">
              <span class="logo pull-right">
                <img
                  src="//cdn.runrepeat.com/img/marketing-calculator/logo-dark.png"
                  alt="RunRepeat logo"
                />
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="marketing-table-results">
      <div class="row">
        <div class="col-xs-12">
          <h3 v-if="racesCount > 0">
            Showing {{ racesCount }} races
            <span v-if="selectedStateName">in {{ selectedStateName }}</span>
          </h3>
        </div>
      </div>
      <races-table
        :filters="filters"
        :ajax-url="ajaxUrl"
        v-on:races-count="updateRacesCount"
      ></races-table>
    </div>
    <div class="marketing-races-footer"></div>
  </div>
</template>

<script>
import DatamapUs from './DatamapUs';
import DatePicker from 'vue-datepicker-next';
import RrMultiselect from '~/components/core/RrMultiselect';
import RacesTable from './RacesTable';
import { mapState } from 'pinia';
import { useMarketingZipcodeStore } from "~/stores/marketingZipcode";
import { getBaseUrl } from "~/utils/apiRequests";

export default {
  name: 'DogRaces',
  components: {
    DatamapUs,
    DatePicker,
    RrMultiselect,
    RacesTable,
  },
  mounted() {
    this.stateFiltersChanged();
  },
  data() {
    return {
      states: [],
      cities: [],
      activeState: '',
      options: {
        state: window['dog_races_filters'].state.options,
        city: [],
        type: window['dog_races_filters'].type.options,
        allowance: window['dog_races_filters'].allowance.options,
      },
      filters: {
        dates: [],
        allowance: [],
        type: [],
        city: [],
        state: [],
      },
      zipcode: '',
      racesCount: 100,
      ajaxUrl: getBaseUrl() + '/marketing-tools/dog-races',
    };
  },
  computed: {
    selectedStateName() {
      return this.activeState
        ? this.options.state.find(s => s.value === this.activeState).name
        : '';
    },
    ...mapState(useMarketingZipcodeStore, {
      zipcodeState: 'zipcodeState',
    }),
  },
  watch: {
    zipcodeState(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.activeState = newValue;
      }
    },
    activeState(newValue, oldValue) {
      if (
        (!newValue && this.filters.state.length > 0) ||
        (newValue && (this.filters.state.length === 0 || this.filters.state[0] !== newValue))
      ) {
        this.filters.state = newValue ? [newValue] : [];
      }
      const selectedState = this.states.find(s => s.value === newValue);
      this.cities = selectedState ? selectedState.cities : [];
    },
    'filters.state': function(newValue, oldValue) {
      if (this.activeState && (!newValue || newValue.length === 0)) {
        this.activeState = '';
      } else if (newValue && newValue.length > 0 && this.activeState !== newValue[0]) {
        this.activeState = newValue[0];
      }
    },
    cities(newValue, oldValue) {
      let cityOptions = [];
      newValue.forEach(city => {
        let racesText = city.cnt === 1 ? '1 race' : city.cnt + ' races';
        cityOptions.push({
          value: city.city,
          name:
            '<span class="city-name">' +
            city.city +
            '</span>, <span class="city-races">' +
            racesText +
            '</span>',
        });
      });
      this.options.city = cityOptions;
    },
    'filters.dates': function(newValue, oldValue) {
      this.stateFiltersChanged();
    },
    'filters.allowance': function(newValue, oldValue) {
      this.stateFiltersChanged();
    },
    'filters.type': function(newValue, oldValue) {
      this.stateFiltersChanged();
    },
    zipcode: function(newValue, oldValue) {
      if (newValue.length === 5) {
        useMarketingZipcodeStore().updateZipcodeState(newValue);
      }
    },
  },
  methods: {
    async stateFiltersChanged() {
      const data = await $api('/marketing-tools/dog-races-states', {
          params: {
            date_start: this.filters.dates[0],
            date_end: this.filters.dates[1],
            'type[]': this.filters.type,
            'allowance[]': this.filters.allowance,
          },
        });

      if (data.hasOwnProperty('states')) {
        this.states = data.states;
      }
    },
    updateRacesCount(count) {
      this.racesCount = count;
    },
  },
};
</script>

<style lang="scss">
//@import 'vue-datepicker-next/scss/index.scss';
@import 'vue-datepicker-next/index.css';

.marketing-table-container {

}
</style>
